import { useCallback, useState, type ReactNode } from 'react';
import { ExternalLink, FileText, Loader2 } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import {
  OptimizedResumePreviewModal,
  type OptimizedResumePreviewModalPayload,
} from '@modules/role-fit/OptimizedResumePreviewModal';
import { loadResumePreviewPayloadFromStoragePath } from '@modules/role-fit/fetchResumePreviewPayload';
import { Modal } from '@shared/components/ui/Modal';

export type CandidateResumePreviewButtonProps = {
  storagePath: string;
  fullName: string;
  currentTitle?: string | null;
  /** Shown under the title in the preview modal header. */
  eyebrow?: string;
  label?: ReactNode;
  className?: string;
  /** `link` matches inline résumé anchors; `neutral` is a subtle bordered control. */
  variant?: 'link' | 'neutral';
};

function buildHeadline(fullName: string, currentTitle?: string | null) {
  const t = currentTitle?.trim();
  const n = fullName.trim();
  if (t && n) return `${t} — ${n}`;
  return n || t || 'Résumé';
}

type PreviewState =
  | { kind: 'closed' }
  | { kind: 'ready'; payload: OptimizedResumePreviewModalPayload; headline: string }
  | { kind: 'error'; message: string; headline: string };

export function CandidateResumePreviewButton({
  storagePath,
  fullName,
  currentTitle,
  eyebrow = 'Primary résumé',
  label = 'View résumé',
  className = '',
  variant = 'link',
}: CandidateResumePreviewButtonProps) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<PreviewState>({ kind: 'closed' });
  const [externalHref, setExternalHref] = useState<string | null>(null);

  const open = useCallback(async () => {
    setPreview({ kind: 'closed' });
    setBusy(true);
    const headline = buildHeadline(fullName, currentTitle);
    try {
      const result = await loadResumePreviewPayloadFromStoragePath(storagePath);
      if (result.mode === 'external') {
        setExternalHref(result.href);
        return;
      }
      setPreview({ kind: 'ready', payload: result.payload, headline });
    } catch (e) {
      setPreview({
        kind: 'error',
        message: e instanceof Error ? e.message : 'Could not load résumé',
        headline,
      });
    } finally {
      setBusy(false);
    }
  }, [storagePath, fullName, currentTitle]);

  const closePreview = useCallback(() => setPreview({ kind: 'closed' }), []);
  const closeExternal = useCallback(() => setExternalHref(null), []);

  const baseBtn =
    variant === 'neutral'
      ? 'inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--vscode-border-primary)] px-3 py-2 text-sm font-medium vscode-text-primary hover:bg-[var(--vscode-bg-hover)] transition-colors disabled:opacity-50 disabled:pointer-events-none'
      : 'inline-flex items-center gap-2 text-sm font-semibold text-brand-400 hover:text-brand-300 disabled:opacity-50 disabled:pointer-events-none';

  return (
    <div className="inline-flex flex-col items-start gap-1">
      <button type="button" className={`${baseBtn} ${className}`.trim()} onClick={open} disabled={busy}>
        {busy ? <Loader2 className="w-4 h-4 shrink-0 animate-spin" /> : <FileText className="w-4 h-4 shrink-0" />}
        {label}
      </button>

      <OptimizedResumePreviewModal
        open={preview.kind !== 'closed'}
        onClose={closePreview}
        error={preview.kind === 'error' ? preview.message : null}
        headline={preview.kind === 'closed' ? '' : preview.headline}
        payload={preview.kind === 'ready' ? preview.payload : null}
        eyebrow={eyebrow}
        consultancyWatermark={user?.orgName ?? null}
        downloadFileBase={fullName}
      />

      <Modal
        open={externalHref !== null}
        onClose={closeExternal}
        title="Open résumé"
        subtitle="This format cannot be previewed in the app"
        icon={<FileText />}
        size="md"
      >
        <p className="text-sm vscode-text-secondary mb-4">
          Word and some other formats open best in a new tab or in your desktop app. Use the button below to download or
          open the file.
        </p>
        <div className="flex flex-wrap gap-2 justify-end">
          <button type="button" className="btn-secondary" onClick={closeExternal}>
            Cancel
          </button>
          <a
            href={externalHref ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
            onClick={closeExternal}
          >
            <ExternalLink className="w-4 h-4" />
            Open in new tab
          </a>
        </div>
      </Modal>
    </div>
  );
}
